import type { ReactNode } from "react";
import type { Address, Parcel, RateOption, WalletSummary } from "@/lib/types";
import { money, transitCopy } from "@/lib/format";
import { Card, cx } from "@/components/ui";

/**
 * What the customer is buying, beside the rates rather than three steps back.
 *
 * It shows the two addresses, what is in the box, what the price is made of,
 * and -- once a service is picked -- what the wallet covers and what is left
 * to pay.
 *
 * The lines come from the server already itemised, and each one is a
 * difference between two prices rather than a share of our cost (see
 * pricing.Breakdown). So a customer can read every charge on their own bill
 * -- the shipping, a surcharge for a hard-to-reach address, the cover they
 * ticked -- while the derivation stays ours: no per-kilometre rate, no levy,
 * no margin. This component adds the lines up and renders them; it never
 * computes one.
 */
export function BookingSummary({
  collection,
  delivery,
  parcels,
  selected,
  wallet,
  action,
  className,
}: {
  collection: Address;
  delivery: Address;
  parcels: Parcel[];
  selected: RateOption | null;
  wallet?: WalletSummary;
  /** The step's primary action, when there is one. It belongs here, under the
   *  figure it is committing to, rather than in a card of its own. */
  action?: ReactNode;
  className?: string;
}) {
  const totalWeight = parcels.reduce((sum, parcel) => sum + Number(parcel.weight_g || 0), 0);

  // What the wallet can take off this booking, and what the card has to cover.
  // A wallet in credit never covers more than the price, and a negative
  // available balance covers nothing.
  const available = Math.max(wallet?.available_cents ?? 0, 0);
  const price = selected?.price_cents ?? 0;
  const fromWallet = Math.min(available, price);
  const dueNow = Math.max(price - fromWallet, 0);

  return (
    <Card className={cx("lg:sticky lg:top-24", className)}>
      <h2 className="text-lg font-semibold tracking-tight">Summary</h2>

      <dl className="mt-4 space-y-3 text-sm">
        <Line term="Collect from" detail={place(collection)} />
        <Line term="Deliver to" detail={place(delivery)} />
        <Line
          term="Parcels"
          detail={`${parcels.length} ${parcels.length === 1 ? "parcel" : "parcels"}, ${(totalWeight / 1000).toFixed(2)} kg`}
        />
        {/* The breakdown's first line already names the service, so this row
            only earns its place when there is no breakdown. */}
        {selected && !selected.lines?.length && <Line term="Service" detail={selected.service_name} />}
        {selected && <Line term="Transit" detail={transitCopy(selected.transit_days)} />}
      </dl>

      <div className="mt-5 border-t border-ink-200 pt-4">
        {selected ? (
          <>
            {/* Itemised when the server said what the price is made of, which
                is every quote from here on. A single total is the fallback for
                a quote taken before the breakdown existed. */}
            {selected.lines?.length ? (
              <div className="mb-2 space-y-1 border-b border-ink-200 pb-2">
                {selected.lines.map((line, index) => (
                  <Row key={`${line.label}-${index}`} label={line.label} value={money(line.amount_cents)} muted />
                ))}
              </div>
            ) : null}
            <Row label="Shipment total" value={money(price)} />
            {/* The price is VAT inclusive, so this is a note and not a line:
                adding it would make the breakdown sum to more than the total.
                Hidden while the business is not VAT registered, which is the
                zero the server sends today. */}
            {selected.vat_cents > 0 && (
              <p className="pb-1 text-xs text-ink-400">Includes {money(selected.vat_cents)} VAT</p>
            )}
            {wallet && (
              <>
                <Row label="Wallet balance" value={money(wallet.available_cents)} muted />
                {fromWallet > 0 && <Row label="Paid from wallet" value={`-${money(fromWallet)}`} muted />}
              </>
            )}
            <div className="mt-3 flex items-baseline justify-between border-t border-ink-200 pt-3">
              <span className="text-sm font-medium">{dueNow > 0 ? "Due now" : "To pay"}</span>
              <span className="text-2xl font-semibold tracking-tight">
                {money(wallet ? dueNow : price)}
              </span>
            </div>
            {wallet && dueNow > 0 && (
              <p className="mt-2 text-xs text-ink-500">
                {fromWallet > 0
                  ? "Your wallet covers part of this. You will pay the rest by card to confirm the booking."
                  : "You will pay by card to confirm the booking."}
              </p>
            )}
            {wallet && dueNow === 0 && (
              <p className="mt-2 text-xs text-ink-500">Paid from your wallet. Nothing to enter.</p>
            )}
            {action && <div className="mt-4">{action}</div>}
          </>
        ) : (
          <p className="text-sm text-ink-500">Choose a service to see the total.</p>
        )}
      </div>
    </Card>
  );
}

/** A suburb and city is enough to recognise an address you typed yourself. */
function place(address: Address) {
  const parts = [address.suburb, address.city].filter(Boolean);
  return parts.length ? parts.join(", ") : "Not set";
}

function Line({ term, detail }: { term: string; detail: string }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-ink-400">{term}</dt>
      <dd className="mt-0.5 text-ink-700">{detail}</dd>
    </div>
  );
}

function Row({ label, value, muted = false }: { label: string; value: string; muted?: boolean }) {
  return (
    <div className={cx("flex items-baseline justify-between py-1 text-sm", muted && "text-ink-500")}>
      <span>{label}</span>
      <span className={muted ? "" : "font-medium"}>{value}</span>
    </div>
  );
}
